<template>
  <div class="deal-card" v-if="deal" :class="{ 'deal-card--button': componentType === 'button' }">
    <router-link
      :to="{ name: 'DealInfo', params: { dealId: deal._id } }"
      v-if="componentType === 'link'"
      class="deal-card__link"
    >
      <div class="deal-card__header">
        <div class="deal-card__logo">
          <img :src="deal.image" :alt="deal.name" loading="lazy" />
        </div>
        <div class="deal-card__info">
          <h3 class="deal-card__name">{{ deal.name }}</h3>
          <OfferingStatusBadge :deal="deal" />
        </div>
      </div>

      <div class="deal-card__progress">
        <div class="deal-card__progress-bar">
          <div 
            class="deal-card__progress-fill" 
            :style="{ width: `${Math.min(totalRaisedPercentage, 100)}%` }"
          ></div>
        </div>
        <div class="deal-card__progress-info">
          <span class="deal-card__percentage">
            {{ totalRaisedPercentage !== 0 ? totalRaisedPercentage.toFixed(1) : 0 }}% raised
          </span>
          <span class="deal-card__amount">
            {{ formatAmountToDollar(totalRaised, 2, currency) }}
            <span class="deal-card__target">
              of {{ formatAmountToDollar(raiseTarget, 2, currency) }}
            </span>
          </span>
        </div>
      </div>

      <div class="deal-card__footer">
        <div class="deal-card__stat">
          <span class="deal-card__stat-label">Min. Investment</span>
          <span class="deal-card__stat-value">{{ formatAmountToDollar(deal.min_trade.buy, 2, currency) }}</span>
        </div>
        <div class="deal-card__stat deal-card__stat--right">
          <span class="deal-card__stat-label">Investment Type</span>
          <span class="deal-card__stat-value">
            {{ deal.investment_type }}<template v-if="offeringTypeLabel"> · {{ offeringTypeLabel }}</template>
          </span>
        </div>
      </div>
    </router-link>

    <div v-else class="deal-card__content">
      <div class="deal-card__header">
        <div class="deal-card__logo">
          <img :src="deal.image" :alt="deal.name" loading="lazy" />
        </div>
        <div class="deal-card__info">
          <h3 class="deal-card__name">{{ deal.name }}</h3>
          <OfferingStatusBadge :deal="deal" />
        </div>
      </div>

      <div class="deal-card__progress">
        <div class="deal-card__progress-bar">
          <div 
            class="deal-card__progress-fill" 
            :style="{ width: `${Math.min(totalRaisedPercentage, 100)}%` }"
          ></div>
        </div>
        <div class="deal-card__progress-info">
          <span class="deal-card__percentage">{{ totalRaisedPercentage }}% raised</span>
          <span class="deal-card__amount">
            ${{ convertToLocale(totalRaisedPercentage) }}
            <span class="deal-card__target">
              of ${{ deal.token?.raise_amount > 0 
                ? convertToLocale(deal.token.raise_amount) 
                : deal.raise_amount > 0 
                  ? convertToLocale(deal.raise_amount) 
                  : '0' }}
            </span>
          </span>
        </div>
      </div>

      <div class="deal-card__footer">
        <div class="deal-card__stat">
          <span class="deal-card__stat-label">Min. Investment</span>
          <span class="deal-card__stat-value">{{ formatAmountToDollar(deal.min_trade.buy, 2, "USD") }}</span>
        </div>
        <div class="deal-card__stat deal-card__stat--right">
          <template v-if="deal.investment_type === 'Commodity'">
            <span class="deal-card__stat-label">Price per token</span>
            <span class="deal-card__stat-value">{{ commodityPrice ? formatAmountToDollar(commodityPrice, 2, currency) : "—" }}</span>
          </template>
          <template v-else>
            <span class="deal-card__stat-label">Market Cap</span>
            <span class="deal-card__stat-value">${{ deal.valuation ? convertToLocale(deal.valuation) : 0 }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
  import { convertToLocale, dealRaiseProgress, formatAmountToDollar } from "@/utils/helpers.js";
import { computed } from "vue";
import OfferingStatusBadge from "@/components/general/OfferingStatusBadge.vue";
import { OfferingTypes, OFFERING_TYPE_LABELS, offeringSource } from "@/utils/offering";
  const props = defineProps({
    deal: {
      type: Object,
      required: true,
      default: () => {},
    },
    componentType: {
      type: String,
      required: false,
      default: () => "link",
    },
  });
  const deal = computed(() => props.deal);
  // Always shown, including "Fixed price". Hiding the default meant every existing deal showed
  // nothing at all, which reads as a missing feature rather than a deliberate omission — and a
  // sponsor who has just chosen how their deal prices should see it reflected back.
  const offeringTypeLabel = computed(() => {
    const type = offeringSource(props.deal)?.offering_type || OfferingTypes.FIXED_PRICE;
    return OFFERING_TYPE_LABELS[type] || "";
  });
  const progress = computed(() => dealRaiseProgress(deal.value));
  const totalRaised = computed(() => progress.value.raised);
  const raiseTarget = computed(() => progress.value.target);
  const totalRaisedPercentage = computed(() => progress.value.percentage);
  const currency = computed(() => deal.value.currency || 'USD');
  const commodityPrice = computed(() => Number(deal.value.token?.price?.exchange ?? deal.value.token?.price?.buy ?? deal.value.price?.exchange ?? deal.value.price?.buy) || 0);
</script>
<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$success: #10B981;
$warning: #D97706;
$error: #DC2626;

.deal-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: $accent-primary;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }

  &--button {
    background-color: $bg-subtle;
    border: 1px solid $border-color;

    &:hover {
      cursor: pointer;
      border-color: $accent-primary;
    }
  }

  &__link,
  &__content {
    display: block;
    padding: 16px;
    text-decoration: none;
    color: inherit;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: $bg-subtle;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__name {
    font-size: 15px;
    font-weight: 500;
    color: $text-primary;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
  }


  &__progress {
    margin-bottom: 16px;
  }

  &__progress-bar {
    height: 5px;
    background: $bg-subtle;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  &__progress-fill {
    height: 100%;
    background: $accent-primary;
    border-radius: 3px;
    transition: width 0.5s ease-out;
  }

  &__progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__percentage {
    font-size: 12px;
    font-weight: 500;
    color: $accent-primary;
  }

  &__amount {
    font-size: 12px;
    font-weight: 500;
    color: $text-primary;
  }

  &__target {
    color: $text-muted;
    font-weight: 400;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    padding-top: 14px;
    border-top: 1px solid $border-color;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &--right {
      text-align: right;
    }
  }

  &__stat-label {
    font-size: 12px;
    color: $text-muted;
  }

  &__stat-value {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
  }
}
</style>
